import { hasVerifiedGangnamRoadGeometry } from './gangnamRoadReference.js'
import { SHUTTLE_STOPS } from './stops.js'
import { SHUTTLE_SEGMENT_GEOMETRY, SHUTTLE_ROAD_REFERENCES } from './segments.js'
import { CAMPUS_LOOP_STOP_IDS, getTripStopIds } from './trips.js'

export { CAMPUS_LOOP_STOP_IDS, getTripStopIds, SHUTTLE_SEGMENT_GEOMETRY }

const isDev = Boolean(import.meta.env?.DEV)
const warnedMissingSegments = new Set()

function distanceMeters(a, b) {
  if (!a || !b) return 0
  const lat = ((a.lat + b.lat) / 2) * Math.PI / 180
  const dLat = (b.lat - a.lat) * 111320
  const dLng = (b.lng - a.lng) * 111320 * Math.cos(lat)
  return Math.sqrt(dLat * dLat + dLng * dLng)
}

function hasCoord(stop) {
  return Number.isFinite(stop?.lat) && Number.isFinite(stop?.lng)
}

function pointFromStop(stop) {
  return { lat: stop.lat, lng: stop.lng }
}

function segmentKey(fromId, toId) {
  return `${fromId}->${toId}`
}

function snapSegmentGeometry(points, fromStop, toStop) {
  if (!Array.isArray(points) || points.length < 2) return null
  return [
    pointFromStop(fromStop),
    ...points.slice(1, -1).filter(hasCoord).map(point => ({ lat: point.lat, lng: point.lng })),
    pointFromStop(toStop),
  ]
}

function resolveSegment(fromId, toId) {
  const fromStop = SHUTTLE_STOPS[fromId]
  const toStop = SHUTTLE_STOPS[toId]
  if (!hasCoord(fromStop) || !hasCoord(toStop)) {
    if (isDev) console.warn(`[shuttle] stop 좌표 누락: ${fromId} -> ${toId}`)
    return { fromId, toId, path: null, drawable: false, reason: 'missing_stop_coord' }
  }

  const key = segmentKey(fromId, toId)
  const reverseKey = segmentKey(toId, fromId)
  const directGeometry = SHUTTLE_SEGMENT_GEOMETRY[key]
  const reverseGeometry = SHUTTLE_SEGMENT_GEOMETRY[reverseKey]

  const roadReference = SHUTTLE_ROAD_REFERENCES[key]
  if (key === 'gangnam-station->campus-sh') {
    const valid = hasVerifiedGangnamRoadGeometry(directGeometry, roadReference) && Array.isArray(directGeometry) && directGeometry.length >= 3
      && directGeometry.every(point => hasCoord(point) && point.lat >= 33 && point.lat <= 39 && point.lng >= 124 && point.lng <= 132)
      && distanceMeters(directGeometry[0], fromStop) <= 20
      && distanceMeters(directGeometry.at(-1), toStop) <= 20
    if (!valid) return { fromId, toId, key, path: null, drawable: false, reason: 'road_reference_geometry_gate' }
    return { fromId, toId, key, path: directGeometry.map(point => ({ ...point })), drawable: true, source: 'osrm_road_reference', roadReference }
  }

  if (directGeometry) {
    return { fromId, toId, key, path: snapSegmentGeometry(directGeometry, fromStop, toStop), drawable: true, source: 'override' }
  }

  // A car route is directional (one-way roads); do not invent its reverse.
  if (SHUTTLE_ROAD_REFERENCES[reverseKey]) {
    return { fromId, toId, key: reverseKey, path: null, drawable: false, reason: 'unsupported_road_reference_direction' }
  }

  if (reverseGeometry) {
    return { fromId, toId, key: reverseKey, path: snapSegmentGeometry([...reverseGeometry].reverse(), fromStop, toStop), drawable: true, source: 'override_reverse' }
  }

  const missingKey = segmentKey(fromId, toId)
  if (!warnedMissingSegments.has(missingKey)) {
    warnedMissingSegments.add(missingKey)
    console.warn(`[shuttle] segment geometry 없음 → 직선 fallback 금지: ${fromId} -> ${toId}`)
  }
  return {
    fromId, toId,
    path: null,
    drawable: false,
    source: 'missing_segment',
    distance: distanceMeters(fromStop, toStop),
  }
}

export function buildTripStops(stopIds = []) {
  return stopIds
    .map((id, index) => {
      const stop = SHUTTLE_STOPS[id]
      if (!stop) return null
      const isLast = index === stopIds.length - 1
      return {
        id: stop.id,
        name: stop.name.replace(' 셔틀 타는 곳', ''),
        type: index === 0 ? 'start' : isLast ? 'end' : 'mid',
        markerLabel: stop.name,
        subtext: stop.detail,
        lat: stop.lat,
        lng: stop.lng,
      }
    })
    .filter(Boolean)
}

export function composeTripPath(stopIds = []) {
  const segments = []
  const path = []
  let lastStopId = null

  for (let index = 0; index < stopIds.length - 1; index += 1) {
    const segment = resolveSegment(stopIds[index], stopIds[index + 1])
    segments.push(segment)
    if (!segment.drawable || !segment.path?.length) continue

    // 직전 segment가 누락되어 끊긴 경우 이어붙이지 않고 새 구간으로 push한다
    if (path.length === 0 || lastStopId !== segment.fromId) {
      path.push(...segment.path)
    } else {
      path.push(...segment.path.slice(1))
    }
    lastStopId = segment.toId
  }

  // path/segment geometry 부재와 무관하게, stop 좌표만으로도 path가 끊기지 않게 한다
  const drawable = path.length >= 2
  if (!drawable && isDev) {
    console.warn(`[shuttle] composedPath 생성 실패: stopIds=${stopIds.join(',')}`)
  }
  return {
    drawable,
    segments,
    path: drawable ? path : null,
  }
}

export function buildTripRouteModel(routeId, departureTime, fallbackStopIds = [], options = {}) {
  const stopIds = getTripStopIds(routeId, departureTime, fallbackStopIds, options)
  const stops = buildTripStops(stopIds)
  const composedPath = composeTripPath(stopIds)
  return { stopIds, stops, composedPath, path: composedPath.path, roadReference: composedPath.segments.find(segment => segment.roadReference)?.roadReference }
}

export function shuttleRouteGroupKey(route) {
  if (route?.routeKey) return route.routeKey
  const id = route?.id ?? ''
  const match = id.match(/^(.+)_\d{4}$/)
  return match?.[1] ?? id
}
